(function () {
  const READY_EVENT = 'ReChargeWidget-loaded';
  const PRIMARY_FORM_SELECTOR = '[data-recharge-root="primary"]';
  const FALLBACK_PRIMARY_FORM_SELECTOR = 'product-form[data-productid]:not([data-sticky="1"])';
  const INJECTION_SELECTOR = 'form[action*="/cart/add"]';
  const PRICE_PROXY_SELECTOR = '[data-recharge-price-proxy]';
  const PRICE_SELECTOR = '[id^="price-"]';
  const WRAPPER_SELECTOR = '.rc-container-wrapper';
  const CLEAN_SELECTORS = [
    '.rc_popup__block',
    '.rc_widget',
    '.rc-widget',
    '.rc_widget__option',
    '.rc-tooltip',
    '.rc__modal'
  ];

  const namespace = window.FCT = window.FCT || {};
  const rechargeNS = namespace.rechargeRestart = namespace.rechargeRestart || {};

  if (rechargeNS.initialized) {
    return;
  }
  rechargeNS.initialized = true;

  let restartTimer = null;
  let pendingConfig = null;
  let lastProductId = null;
  let activeObserver = null;

  function onRechargeReady(fn) {
    if (window.ReChargeWidget) {
      fn();
      return;
    }
    document.addEventListener(READY_EVENT, fn, { once: true });
  }

  function disconnectObserver() {
    if (activeObserver) {
      activeObserver.disconnect();
      activeObserver = null;
    }
  }

  function protectPriceElements(root = document) {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    const priceBlocks = Array.from(root.querySelectorAll(PRICE_SELECTOR));
    priceBlocks.forEach((priceEl) => {
      if (priceEl.__rechargePriceObserver) return;
      const updateSnapshot = () => {
        if (priceEl.querySelector('.amount')) {
          priceEl.dataset.priceSnapshot = priceEl.innerHTML;
        }
      };
      updateSnapshot();
      const observer = new MutationObserver(() => {
        const rechargeEl = priceEl.querySelector('.recharge-inner-most-price');
        if (rechargeEl && priceEl.children.length === 1) {
          const snapshot = priceEl.dataset.priceSnapshot;
          if (snapshot && snapshot.trim() !== '') {
            if (priceEl.__rechargeRestoring) return;
            priceEl.__rechargeRestoring = true;
            priceEl.innerHTML = snapshot;
            priceEl.__rechargeRestoring = false;
          }
          return;
        }
        updateSnapshot();
      });
      observer.observe(priceEl, { childList: true, subtree: true });
      priceEl.__rechargePriceObserver = observer;
    });
  }

  function monitorWrappers(form) {
    disconnectObserver();
    if (!form || typeof MutationObserver !== 'function') return;
    // Recharge occasionally injects multiple wrapper nodes; trim anything beyond the first.
    activeObserver = new MutationObserver(() => {
      const wrappers = form.querySelectorAll(WRAPPER_SELECTOR);
      wrappers.forEach((node, index) => {
        if (index > 0) node.remove();
      });
    });
    activeObserver.observe(form, { childList: true, subtree: true });
  }

  function getPrimaryForm(root) {
    if (root && typeof root.querySelector === 'function') {
      const scoped = root.querySelector(PRIMARY_FORM_SELECTOR);
      if (scoped) return scoped;
      const fallbackScoped = root.querySelector(FALLBACK_PRIMARY_FORM_SELECTOR);
      if (fallbackScoped) return fallbackScoped;
    }
    return (
      document.querySelector(PRIMARY_FORM_SELECTOR) ||
      document.querySelector(FALLBACK_PRIMARY_FORM_SELECTOR)
    );
  }

  function ensureElementSelector(element) {
    if (!element || !(element instanceof Element)) return null;
    if (!element.id) {
      element.id = `recharge-parent-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    }
    return `#${element.id}`;
  }

  // Read the current PDP markup to figure out where Recharge should attach.
  function extractConfig(root) {
    const form = getPrimaryForm(root);
    if (!form) return null;

    const productId = Number(form.getAttribute('data-productid'));
    if (!Number.isFinite(productId)) return null;

    const injectionParent =
      form.querySelector(INJECTION_SELECTOR) ||
      form;

    return {
      productId,
      form,
      injectionParent: injectionParent instanceof Element ? injectionParent : form,
      selectors: {
        variant: ['input[name="id"]'],
        price: [PRICE_PROXY_SELECTOR],
      },
    };
  }

  function cleanup(form) {
    disconnectObserver();
    if (form && typeof form.querySelectorAll === 'function') {
      form.querySelectorAll(WRAPPER_SELECTOR).forEach((node) => node.remove());
    }
    document.querySelectorAll(WRAPPER_SELECTOR).forEach((node) => {
      if (!form || !form.contains(node)) {
        node.remove();
      }
    });

    CLEAN_SELECTORS.forEach((selector) => {
      if (form && typeof form.querySelectorAll === 'function') {
        form.querySelectorAll(selector).forEach((node) => node.remove());
      }
      document.querySelectorAll(selector).forEach((node) => {
        if (!form || !form.contains(node)) {
          node.remove();
        }
      });
    });
  }

  function destroyExistingWidgets() {
    try {
      window.ReChargeWidget?.destroyWidgets?.();
    } catch (error) {
      console.error(error);
    }
    try {
      window.Interface?.destroyAllWidgets?.();
    } catch (error) {
      console.error(error);
    }
  }

  function createWidget(config) {
    const { productId, injectionParent, selectors } = config;
    const parentElement = injectionParent instanceof Element ? injectionParent : null;
    const targetSelector = parentElement ? ensureElementSelector(parentElement) : null;

    if (window.ReChargeWidget?.createWidget) {
      window.ReChargeWidget.createWidget({
        productId,
        injectionParent: parentElement || targetSelector || injectionParent,
        injectionMethod: 'prepend',
        selectors,
      });
      return;
    }

    if (window.Interface?.createWidget) {
      const targetNode = parentElement || (targetSelector ? document.querySelector(targetSelector) : null);
      if (targetNode) {
        window.Interface.createWidget(targetNode);
      }
    }
  }

  function executeRestart(config) {
    restartTimer = null;
    if (!config || !config.productId) return;

    // Recharge exposes a global event once its runtime is ready; only then is it safe to re-create widgets.
    onRechargeReady(() => {
      const { productId, form } = config;
      if (productId === lastProductId) {
        cleanup(form);
        monitorWrappers(form);
        return;
      }

      cleanup(form);
      destroyExistingWidgets();
      createWidget(config);
      monitorWrappers(form);

      lastProductId = productId;
    });
  }

  function scheduleRestart(config) {
    if (!config || !config.productId) return;
    pendingConfig = config;
    clearTimeout(restartTimer);
    restartTimer = setTimeout(() => executeRestart(pendingConfig), 60);
  }

  document.addEventListener('pdp:swap:start', () => {
    disconnectObserver();
  });

  document.addEventListener('pdp:swap:success', (event) => {
    const config = extractConfig(event?.detail?.container);
    if (config) {
      scheduleRestart(config);
    }
    protectPriceElements(event?.detail?.container || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => protectPriceElements(document), { once: true });
  } else {
    protectPriceElements(document);
  }
})();
